import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Calendar, ArrowRight, Star } from "lucide-react";
import { api } from "../../services/api";
import { setStoredAstriaJapanDob } from "../../utils/astriaJapanKyusei";

// ─── Astria Japan Kyusei — "New Tab" JP UI Style Guide (client spec):
//   Container: background #FFFFFF, padding 32, radius 16,
//              shadow 0 8px 24px rgba(0,0,0,0.06), maxWidth 720
//   Section block: padding 24, radius 12, background #FAFAFA
//   Typography: title 22-24px/weight 500/#222, body 15px/#444
//   Language support: Japanese, Korean or English based on origin/user preference

const JP_FONT = "'Noto Sans JP', sans-serif";
const KR_FONT = "'Noto Sans KR', sans-serif";
const EN_FONT = "'Inter', sans-serif";

const C = {
  bg: "linear-gradient(160deg, #FFFDFB 0%, #FBF3EF 100%)",
  card: "#FFFFFF",
  section: "#FBF3EF",
  cardBorder: "#F3E4DC",
  accent: "#D4735D",
  accentSoft: "rgba(212,115,93,0.12)",
  accentGradient: "linear-gradient(135deg, #D4735D 0%, #E0796B 100%)",
  titleColor: "#2B211D",
  bodyColor: "#4A3B33",
  mutedColor: "#8A7469",
  error: "#B4574A",
  success: "#6FAE8C",
};

// ─── Date of Birth formatting helpers ─────────────────────────────────

const formatDateValue = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const limitedDigits = digits.slice(0, 8);
  let formatted = "";
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 2 || i === 4) formatted += "/";
    formatted += limitedDigits[i];
  }
  return formatted;
};

const validateDateValue = (value: string): string => {
  if (!value) return value;
  const parts = value.split("/");
  if (parts.length === 3) {
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);
    let year = parseInt(parts[2], 10);
    if (isNaN(day)) day = 1;
    if (day < 1) day = 1;
    if (day > 31) day = 31;
    if (isNaN(month)) month = 1;
    if (month < 1) month = 1;
    if (month > 12) month = 12;
    if (isNaN(year)) year = new Date().getFullYear();
    if (year < 1900) year = 1900;
    if (year > 2020) year = 2020;
    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  }
  return value;
};

// ─── Language Detection ─────────────────────────────────────────────────
const getPreferredLanguage = (): "ko" | "en" => {
  try {
    // Check if origin is Korea
    const origin = localStorage.getItem("userRegion") || 
                   sessionStorage.getItem("userRegion") || 
                   "other";
    
    // If origin is Korea, use Korean
    if (origin === "korea" || origin === "kr" || origin === "KO") {
      return "ko";
    }
    
    // Otherwise check stored language preference
    const lang = localStorage.getItem("preferredLanguage") || "en";
    return lang === "ko" ? "ko" : "en";
  } catch {
    return "en";
  }
};

interface KyuseiStrings {
  brand: string;
  title: string;
  subtext: string;
  dobLabel: string;
  dobPlaceholder: string;
  dobRequiredError: string;
  genericError: string;
  submitButton: string;
  loadingButton: string;
  footer: string;
  hint: string;
}

type JPKyuseiMode = "daily" | "timing";

const MODE_META: Record<
  JPKyuseiMode,
  {
    title: Record<"ko" | "en", string>;
    subtext: Record<"ko" | "en", string>;
    resultPath: string;
    endpoint: string;
    icon: React.ReactNode;
  }
> = {
  daily: {
    title: {
      en: "Kyusei & the Flow of Your Mind",
      ko: "구성과 마음의 흐름",
    },
    subtext: {
      en: "Today, let's quietly look at your star and the timing of your heart.",
      ko: "오늘, 당신의 별과 마음의 타이밍을 조용히 살펴보세요.",
    },
    resultPath: "/astria-japan/kyusei/result",
    endpoint: "/backend/astria-japan-kyusei/daily",
    icon: <Star className="w-5 h-5" style={{ color: C.accent }} />,
  },
  timing: {
    title: {
      en: "Today's Heart Timing",
      ko: "오늘의 마음 타이밍",
    },
    subtext: {
      en: "A quiet space to sense the gentle flow of your heart and its natural timing.",
      ko: "마음의 부드러운 흐름과 자연스러운 타이밍을 느끼기 위한 조용한 공간입니다.",
    },
    resultPath: "/astria-japan/timing/result",
    endpoint: "/backend/astria-japan-kyusei/timing",
    icon: <Sparkles className="w-5 h-5" style={{ color: C.accent }} />,
  },
};

const STRINGS: Record<"ko" | "en", KyuseiStrings> = {
  en: {
    brand: "Kyusei",
    title: "Check Your Today's Flow",
    subtext:
      "Enter your date of birth to discover your star and heart's gentle timing.",
    dobLabel: "Date of Birth",
    dobPlaceholder: "DD / MM / YYYY",
    dobRequiredError: "Please enter a valid date of birth.",
    genericError: "Something went wrong. Please try again.",
    submitButton: "See Today's Flow",
    loadingButton: "Quietly preparing…",
    footer: "A gentle moment for your heart.",
    hint: "We'll use this to read your star's energy for today.",
  },
  ko: {
    brand: "구성",
    title: "오늘의 흐름 확인하기",
    subtext:
      "생년월일을 입력하여 당신의 별과 마음의 부드러운 타이밍을 발견하세요.",
    dobLabel: "생년월일",
    dobPlaceholder: "YY / MM / DD",
    dobRequiredError: "올바른 생년월일을 입력해주세요.",
    genericError: "문제가 발생했습니다. 다시 시도해주세요.",
    submitButton: "오늘의 흐름 보기",
    loadingButton: "조용히 준비 중…",
    footer: "당신의 마음에 조용히 다가가는 시간.",
    hint: "오늘 당신의 별 에너지를 읽기 위해 사용됩니다.",
  },
};

const DateOfBirthField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  t: KyuseiStrings;
  lang: "ko" | "en";
}> = ({ value, onChange, t, lang }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const oldFormatted = rawValue;
    const newFormatted = formatDateValue(rawValue);
    onChange(newFormatted);
    setTimeout(() => {
      if (input === document.activeElement) {
        let newCursorPos = cursorPosition;
        if (cursorPosition === 3 && newFormatted.length > oldFormatted.length)
          newCursorPos = 4;
        else if (
          cursorPosition === 6 &&
          newFormatted.length > oldFormatted.length
        )
          newCursorPos = 7;
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const fontFamily = lang === "ko" ? KR_FONT : EN_FONT;

  return (
    <div>
      <label
        className="block text-[11px] font-semibold tracking-[0.15em] uppercase mb-1.5"
        style={{ color: C.mutedColor, fontFamily }}
      >
        {t.dobLabel}
        <span className="ml-1" style={{ color: C.accent }}>
          *
        </span>
      </label>
      <div className="relative">
        <Calendar
          className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4"
          style={{ color: C.mutedColor }}
        />
        <input
          type="text"
          inputMode="numeric"
          placeholder={t.dobPlaceholder}
          value={value}
          onChange={handleChange}
          onBlur={(e) => {
            onChange(validateDateValue(e.target.value));
            e.currentTarget.style.borderColor = C.cardBorder;
            e.currentTarget.style.boxShadow = "none";
          }}
          className="w-full rounded-full border pl-11 pr-4 py-3 text-[15px] shadow-sm focus:outline-none focus:ring-1 transition-all duration-200"
          style={{
            borderColor: C.cardBorder,
            color: C.titleColor,
            background: "#FFFFFF",
            fontFamily,
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = C.accent;
            e.currentTarget.style.boxShadow = `0 0 0 3px ${C.accentSoft}`;
          }}
        />
      </div>
      <p className="mt-2 text-xs" style={{ color: C.mutedColor, opacity: 0.7, fontFamily }}>
        {t.hint}
      </p>
    </div>
  );
};

const KyuseiJapanForm: React.FC<{ mode: JPKyuseiMode }> = ({ mode }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const meta = MODE_META[mode];
  const language = getPreferredLanguage();
  const lang = language === "ko" ? "ko" : "en";
  const t = STRINGS[lang];

  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fontFamily = lang === "ko" ? KR_FONT : EN_FONT;

  const handleSubmit = async () => {
    if (!dob || dob.split("/").length !== 3) {
      setError(t.dobRequiredError);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload: Record<string, string> = { dob };
      const response = await api.post(meta.endpoint, payload);
      if (response.data?.success) {
        const storageKey =
          mode === "daily"
            ? "astriaJapanKyuseiDaily"
            : "astriaJapanKyuseiTiming";
        sessionStorage.setItem(storageKey, JSON.stringify(response.data));
        setStoredAstriaJapanDob(dob);
        const categoryId = searchParams.get("categoryId");
        navigate(
          categoryId
            ? `${meta.resultPath}?categoryId=${categoryId}`
            : meta.resultPath,
        );
      } else {
        setError(response.data?.message || t.genericError);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || t.genericError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: C.bg, fontFamily }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full"
        style={{
          background: C.card,
          borderRadius: 20,
          boxShadow: "0 12px 40px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.02)",
          maxWidth: 480,
          padding: 36,
        }}
      >
        {/* ─── Brand & Icon ────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-4">
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: C.accentGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 16px ${C.accent}44`,
            }}
          >
            {meta.icon}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: C.mutedColor,
              letterSpacing: "0.1em",
              fontFamily,
            }}
          >
            {t.brand}
          </div>
        </div>

        {/* ─── Title ────────────────────────────────────────────────────── */}
        <div
          style={{
            fontSize: 24,
            fontWeight: 600,
            color: C.titleColor,
            marginBottom: 4,
            lineHeight: 1.3,
            fontFamily,
          }}
        >
          {meta.title[lang]}
        </div>
        <p
          style={{
            fontSize: 14,
            color: C.mutedColor,
            marginBottom: 28,
            lineHeight: 1.7,
            fontFamily,
          }}
        >
          {meta.subtext[lang]}
        </p>

        {/* ─── Form Section ────────────────────────────────────────────── */}
        <div style={{ background: C.section, borderRadius: 16, padding: 24 }}>
          <DateOfBirthField value={dob} onChange={setDob} t={t} lang={lang} />
        </div>

        {/* ─── Error Message ───────────────────────────────────────────── */}
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 text-sm flex items-center gap-2"
            style={{ color: C.error, fontFamily }}
          >
            <span className="text-base">✧</span>
            {error}
          </motion.p>
        )}

        {/* ─── Submit Button ───────────────────────────────────────────── */}
        <motion.button
          onClick={handleSubmit}
          disabled={loading}
          className="mt-6 w-full rounded-full py-3.5 text-sm font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 group"
          style={{
            background: C.accentGradient,
            boxShadow: `0 4px 16px ${C.accent}44`,
            opacity: loading ? 0.6 : 1,
            fontFamily,
          }}
          whileHover={{ scale: loading ? 1 : 1.02 }}
          whileTap={{ scale: loading ? 1 : 0.98 }}
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t.loadingButton}
            </>
          ) : (
            <>
              {t.submitButton}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </motion.button>

        {/* ─── Footer ───────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{
            textAlign: "center",
            marginTop: 24,
            paddingTop: 20,
            borderTop: "1px solid rgba(240,232,228,0.3)",
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: C.mutedColor,
              opacity: 0.7,
              letterSpacing: "0.05em",
              fontFamily,
            }}
          >
            {t.footer}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default KyuseiJapanForm;